import { useDataProvider } from "@/providers/DataProvider";
import Button from "react-bootstrap/Button";
import ListGroup from "react-bootstrap/ListGroup";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import Tag from "@/models/Tag";

export const TagListDialog = () => {
  const { tags, updateTag, addTag, deleteTag } = useDataProvider();
  const navigate = useNavigate();
  const onHide = () => navigate(-1);

  // State for editing tags
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState("");
  const [addColor, setAddColor] = useState("");

  const startEdit = (tag: Tag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColor(tag.color || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const saveEdit = (tag: Tag) => {
    updateTag(tag.setName(editName).setColor(editColor));
    setEditingId(null);
    setEditName("");
    setEditColor("");
  };

  const handleDelete = (tag: Tag) => {
    if (
      window.confirm(
        `Are you sure you want to delete the tag "${tag.name}"?`,
      )
    ) {
      deleteTag(tag.id);
    }
  };

  const startAdd = () => {
    setAdding(true);
    setAddName("");
    setAddColor("");
  };

  const cancelAdd = () => {
    setAdding(false);
    setAddName("");
    setAddColor("");
  };

  const saveAdd = () => {
    if (addName.trim() === "") return;
    addTag(new Tag({ name: addName, color: addColor }));
    setAdding(false);
    setAddName("");
    setAddColor("");
  };

  return (
    <Modal show={true} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Tag List</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ListGroup>
          {tags && tags.length > 0 ? (
            tags.map((tag: Tag) => (
              <ListGroup.Item key={tag.id}>
                {editingId === tag.id ? (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Label visuallyHidden>Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        placeholder="Tag Name"
                        autoFocus
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label visuallyHidden>Color</Form.Label>
                      <Form.Control
                        type="color"
                        value={editColor}
                        onChange={(e) => setEditColor(e.target.value)}
                        title="Tag Color"
                      />
                    </Form.Group>
                    <div className="d-flex gap-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => saveEdit(tag)}
                        disabled={editName.trim() === ""}
                      >
                        Save
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={cancelEdit}
                      >
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center gap-2">
                      <span
                        style={{
                          display: "inline-block",
                          width: 18,
                          height: 18,
                          backgroundColor: tag.color || "#ccc",
                          borderRadius: "50%",
                          border: "1px solid #aaa",
                        }}
                        title={tag.color}
                      />
                      <strong>{tag.name}</strong>
                    </div>
                    <div className="d-flex gap-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => startEdit(tag)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(tag)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                )}
              </ListGroup.Item>
            ))
          ) : (
            <div>No tags available.</div>
          )}
        </ListGroup>
        <div className="mt-3">
          {adding ? (
            <div>
              <Form.Group className="mb-2">
                <Form.Label visuallyHidden>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  placeholder="Tag Name"
                  autoFocus
                />
              </Form.Group>
              <Form.Group className="mb-2">
                <Form.Label visuallyHidden>Color</Form.Label>
                <Form.Control
                  type="color"
                  value={addColor}
                  onChange={(e) => setAddColor(e.target.value)}
                  title="Tag Color"
                />
              </Form.Group>
              <div className="d-flex gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={saveAdd}
                  disabled={addName.trim() === ""}
                >
                  Add
                </Button>
                <Button variant="secondary" size="sm" onClick={cancelAdd}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="success" size="sm" onClick={startAdd}>
              + Add Tag
            </Button>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
